import { useAuthStore } from '@/stores/authStore'
import { clearStoredLastVisited, markLoginOpensHome } from '@/lib/lastVisited'
import { useVisionStore } from '@/stores/visionStore'
import type {
  FileResponse,
  LoginRequest,
  MemoryDetail,
  MemoryList,
  PaginatedChats,
  ProjectRead,
  TokenResponse,
  User,
} from '@/types/api'

/**
 * Thin fetch wrapper speaking the backend's /genai/api/v1 API
 * (backend_openapi_spec.json):
 * - attaches `Authorization: Bearer <access>` from the in-memory store;
 * - `credentials: 'include'` ONLY on /auth/* (the refresh cookie must flow);
 * - 401 → single-flight refresh → retry the original request once. N
 *   concurrent 401s trigger exactly one POST /auth/refresh; the rest queue
 *   on the same promise and replay after it resolves.
 */

/**
 * `VITE_API_URL` IS the API base, mount path included — every endpoint path is
 * appended to it verbatim. Vite picks the file it comes from by MODE, not by
 * name: `--mode dev` (pnpm dev / build:dev) reads `.env.dev`, a default
 * `vite build` reads `.env.production` / `.env`. Vitest injects it directly
 * (vite.config.ts) because Node's fetch needs an absolute URL.
 *
 * On the Vite *dev server* an absolute ALB URL is rewritten to its path
 * (`/genai/api/v1`) so fetch stays same-origin and the httpOnly refresh
 * cookie is first-party. Cross-origin localhost → ALB drops SameSite=Lax
 * / third-party cookies (`Refresh token not provided`). Builds keep the
 * absolute URL — `.env.dev` is also used by `build:dev`.
 */
export function resolveApiBase(
  raw: string,
  opts: { dev: boolean; vitest: boolean },
): string {
  const trimmed = raw.trim().replace(/\/+$/, '')
  if (opts.dev && !opts.vitest && /^https?:\/\//i.test(trimmed)) {
    try {
      return new URL(trimmed).pathname.replace(/\/+$/, '') || trimmed
    } catch {
      return trimmed
    }
  }
  return trimmed
}

export const API_URL: string = resolveApiBase(
  import.meta.env.VITE_API_URL ?? '',
  { dev: import.meta.env.DEV, vitest: Boolean(import.meta.env.VITEST) },
)

export const API_BASE = API_URL

/** Staging is the only host that serves `/memories` (user 2026-09-24). */
export const MEMORIES_STAGING_BASE =
  'https://rehrigkgdiot-stg.soulax.io/genai/api/v1'

/**
 * When the app is pointed at the dev ALB, memories still go to staging.
 * On the Vite dev server that is a same-origin `/stg-genai` prefix (see
 * vite.config.ts) so the browser does not make a cross-origin call.
 * Staging deploys and any non-dev base keep the normal API base.
 */
export function resolveMemoriesBase(
  apiUrl: string,
  opts: { dev: boolean; vitest: boolean },
): string {
  if (!apiUrl.includes('rehrigkgdiotapi-dev.soulax.io')) {
    return resolveApiBase(apiUrl, opts)
  }
  if (opts.dev && !opts.vitest) return '/stg-genai/api/v1'
  return MEMORIES_STAGING_BASE
}

export const MEMORIES_API_BASE = resolveMemoriesBase(
  import.meta.env.VITE_API_URL ?? '',
  { dev: import.meta.env.DEV, vitest: Boolean(import.meta.env.VITEST) },
)

// A relative base without its leading slash resolves against the current route
// (/chat/c-1/genai/api/v1/… → S3/404) — silent in prod, so shout in dev.
if (import.meta.env.DEV && API_BASE && !/^(https?:\/\/|\/)/i.test(API_BASE)) {
  console.error(
    `VITE_API_URL must be absolute or start with "/" — got "${API_BASE}"`,
  )
}

export class ApiError extends Error {
  status: number
  body: unknown
  constructor(status: number, body: unknown) {
    const detail =
      typeof body === 'object' && body !== null
        ? (body as Record<string, unknown>).detail ||
          (body as Record<string, unknown>).message
        : null
    super(typeof detail === 'string' ? detail : `API ${status}`)
    this.status = status
    this.body = body
  }
}

function isAuthPath(path: string) {
  return path.startsWith('/auth/')
}

/** Single-flight: all concurrent 401s share this one refresh promise. */
let refreshInFlight: Promise<string | null> | null = null
/** Bumped on logout so an in-flight refresh cannot restore the session. */
let refreshGeneration = 0

async function doRefresh(generation: number): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
    if (!res.ok) {
      useAuthStore.getState().clearAuth()
      return null
    }
    const data = (await res.json()) as TokenResponse
    if (!data.access_token) {
      useAuthStore.getState().clearAuth()
      return null
    }
    if (generation !== refreshGeneration) return null
    useAuthStore.getState().setAuth(data.access_token)
    // TokenResponse carries no profile — restore it for the greeting/user row.
    if (!useAuthStore.getState().user) await fetchMe().catch(() => undefined)
    return data.access_token
  } catch {
    useAuthStore.getState().clearAuth()
    return null
  }
}

export function refreshAccessToken(): Promise<string | null> {
  const generation = refreshGeneration
  refreshInFlight ??= doRefresh(generation).finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

/** Core request with auth + one retry after a successful refresh. */
export async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const exec = async (): Promise<Response> => {
    const token = useAuthStore.getState().accessToken
    const headers = new Headers(init.headers)
    if (token) headers.set('Authorization', `Bearer ${token}`)
    // FormData sets its own multipart boundary — never override it.
    if (
      init.body != null &&
      !(init.body instanceof FormData) &&
      !headers.has('Content-Type')
    ) {
      headers.set('Content-Type', 'application/json')
    }
    return fetch(`${API_BASE}${path}`, {
      ...init,
      headers,
      credentials: isAuthPath(path) ? 'include' : 'same-origin',
    })
  }

  let res = await exec()

  // Logout clears the token first — leftover 401s must not POST /auth/refresh.
  if (
    res.status === 401 &&
    !isAuthPath(path) &&
    useAuthStore.getState().accessToken
  ) {
    const token = await refreshAccessToken()
    if (token === null) throw new ApiError(401, null)
    res = await exec()
  }

  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, body)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const api = {
  get: <T>(path: string, options?: RequestInit) => request<T>(path, options),
  post: <T>(path: string, body?: unknown, options?: RequestInit) =>
    request<T>(path, {
      ...options,
      method: 'POST',
      body:
        body instanceof FormData
          ? body
          : body != null
            ? JSON.stringify(body)
            : undefined,
    }),
  put: <T>(path: string, body?: unknown, options?: RequestInit) =>
    request<T>(path, {
      ...options,
      method: 'PUT',
      body:
        body instanceof FormData
          ? body
          : body != null
            ? JSON.stringify(body)
            : undefined,
    }),
  patch: <T>(path: string, body: unknown, options?: RequestInit) =>
    request<T>(path, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  delete: <T>(path: string, options?: RequestInit) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}

/** Memories may target a different host than `API_BASE`. A 401 there must
 * not refresh or clear the session for the rest of the app. */
async function memoriesGet<T>(path: string): Promise<T> {
  if (MEMORIES_API_BASE === API_BASE) return api.get<T>(path)
  const token = useAuthStore.getState().accessToken
  const headers = new Headers()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  const res = await fetch(`${MEMORIES_API_BASE}${path}`, {
    headers,
    credentials: 'same-origin',
  })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, body)
  }
  return (await res.json()) as T
}

export function listMemories(): Promise<MemoryList> {
  return memoriesGet<MemoryList>('/memories')
}

export function readMemory(name: string): Promise<MemoryDetail> {
  return memoriesGet<MemoryDetail>(`/memories/${encodeURIComponent(name)}`)
}

async function memoriesDelete(path: string): Promise<void> {
  if (MEMORIES_API_BASE === API_BASE) {
    await api.delete(path)
    return
  }
  const token = useAuthStore.getState().accessToken
  const headers = new Headers()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  const res = await fetch(`${MEMORIES_API_BASE}${path}`, {
    headers,
    credentials: 'same-origin',
    method: 'DELETE',
  })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, body)
  }
}

export function deleteMemory(name: string): Promise<void> {
  return memoriesDelete(`/memories/${encodeURIComponent(name)}`)
}

// ── Auth flows (plan §8) ────────────────────────────────────────────────────

/** GET /auth/me has no typed schema — map the profile defensively. */
async function fetchMe(tokenResponse?: TokenResponse): Promise<User> {
  const me = await api
    .get<Record<string, unknown>>('/auth/me')
    .catch(() => ({}) as Record<string, unknown>)
  const str = (v: unknown) => (typeof v === 'string' ? v : undefined)
  const email =
    str(me.email) ?? str(me.login_id) ?? tokenResponse?.login_id ?? ''
  const login_id =
    str(me.login_id) ?? tokenResponse?.login_id ?? (email || undefined)
  const personal_id =
    me.personal_id ?? me.id ?? me.user_id ?? tokenResponse?.personal_id

  const user: User = {
    id: String(personal_id ?? me.user_id ?? login_id ?? email),
    email,
    name:
      str(me.name) ??
      str(me.full_name) ??
      str(me.username) ??
      (email ? email.split('@')[0] : ''),
    org: str(me.org) ?? str(me.organization) ?? str(me.company),
    preferredName: str(me.preferred_name),
    instructions: str(me.custom_instructions),
    structuredResponse:
      typeof me.structured_response === 'boolean'
        ? me.structured_response
        : false,
    login_id,
    personal_id:
      personal_id != null
        ? typeof personal_id === 'number'
          ? personal_id
          : String(personal_id)
        : undefined,
  }
  useAuthStore.getState().setAuth(useAuthStore.getState().accessToken!, user)
  return user
}

export async function login(credentials: LoginRequest): Promise<User | null> {
  const data = await api.post<TokenResponse>('/auth/login', credentials)
  useAuthStore.getState().setAuth(data.access_token)
  clearStoredLastVisited()
  markLoginOpensHome()
  useVisionStore.getState().resetChat()
  // Profile fetch is best-effort — pass TokenResponse so login_id & personal_id are retained if /me fails or returns sparse object.
  return fetchMe(data).catch(() => {
    const fallbackUser: User = {
      id: String(data.personal_id ?? data.login_id ?? credentials.email),
      email: credentials.email,
      name: credentials.email.split('@')[0],
      login_id: data.login_id,
      personal_id: data.personal_id,
    }
    useAuthStore.getState().setAuth(data.access_token, fallbackUser)
    return fallbackUser
  })
}

/** POST /auth/signup — backend emails a verification link; the password is
 * set later via /complete-signup (SignupInput = email only). */
export async function signup(email: string): Promise<void> {
  await api.post('/auth/signup', { email })
}

/** GET /auth/verify-signup?token= — validates the emailed link before the
 * set-password form is shown; returns the email it was issued for. */
export async function verifySignup(token: string): Promise<string> {
  const data = await api.get<{ email: string }>(
    `/auth/verify-signup?token=${encodeURIComponent(token)}`,
  )
  return data.email
}

/** POST /auth/complete-signup — creates the account and logs in. */
export async function completeSignup(
  token: string,
  password: string,
): Promise<User | null> {
  const data = await api.post<TokenResponse>('/auth/complete-signup', {
    token,
    password,
  })
  useAuthStore.getState().setAuth(data.access_token)
  clearStoredLastVisited()
  markLoginOpensHome()
  useVisionStore.getState().resetChat()
  return fetchMe().catch(() => null)
}

export async function forgotPassword(email: string): Promise<void> {
  await api.post('/auth/forgot-password', { email })
}

/** GET /auth/verify-reset-password?token= — same pre-flight as verifySignup. */
export async function verifyResetPassword(token: string): Promise<string> {
  const data = await api.get<{ email: string }>(
    `/auth/verify-reset-password?token=${encodeURIComponent(token)}`,
  )
  return data.email
}

/** POST /auth/reset-password — sets the new password and logs in. */
export async function resetPassword(
  token: string,
  newPassword: string,
): Promise<User | null> {
  const data = await api.post<TokenResponse>('/auth/reset-password', {
    token,
    new_password: newPassword,
    confirm_new_password: newPassword,
  })
  useAuthStore.getState().setAuth(data.access_token)
  clearStoredLastVisited()
  markLoginOpensHome()
  useVisionStore.getState().resetChat()
  return fetchMe().catch(() => null)
}

/** POST /auth/change-password — requires the current password, not just a
 * valid access token, so a stolen live token alone can't lock the owner out. */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await api.post('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
    confirm_new_password: newPassword,
  })
}

/** PATCH /auth/me — updates one profile field at a time (each settings row
 * saves independently). Store update ripples into the sidebar row, avatar
 * initials, and (via `user_preferred_name`/`user_custom_instructions` in the
 * agent's system prompt) the next chat turn. */
export async function updateProfile(fields: {
  name?: string
  preferredName?: string
  instructions?: string
  structuredResponse?: boolean
}): Promise<void> {
  await api.patch('/auth/me', {
    name: fields.name,
    preferred_name: fields.preferredName,
    custom_instructions: fields.instructions,
    structured_response: fields.structuredResponse,
  })
  const user = useAuthStore.getState().user
  if (user) {
    const patch: Partial<User> = {}
    if (fields.name !== undefined) patch.name = fields.name
    if (fields.preferredName !== undefined)
      patch.preferredName = fields.preferredName
    if (fields.instructions !== undefined)
      patch.instructions = fields.instructions
    if (fields.structuredResponse !== undefined)
      patch.structuredResponse = fields.structuredResponse
    useAuthStore
      .getState()
      .setAuth(useAuthStore.getState().accessToken!, { ...user, ...patch })
  }
}

export async function logout(): Promise<void> {
  refreshGeneration += 1
  refreshInFlight = null
  await api.post('/auth/logout').catch(() => undefined) // best-effort
  clearStoredLastVisited()
  useVisionStore.getState().resetChat()
  useAuthStore.getState().clearAuth()
}

/**
 * Startup bootstrap: the access token is memory-only, so on load/refresh we
 * mint one from the refresh cookie before rendering guarded routes. Resolves
 * to true when authenticated. Runs once; concurrent callers share it.
 */
let bootstrapPromise: Promise<boolean> | null = null

export function bootstrapAuth(): Promise<boolean> {
  if (useAuthStore.getState().accessToken) return Promise.resolve(true)
  bootstrapPromise ??= refreshAccessToken()
    .then((token) => token !== null)
    .catch(() => false)
    .finally(() => {
      bootstrapPromise = null
    })
  return bootstrapPromise
}

/** Data controls "Archive all". The contract has no bulk archive route:
 * list active chats, then PATCH each `{ archived: true }`. Returns the
 * archived chat ids (empty when nothing was active). */
export async function archiveAllChats(): Promise<string[]> {
  const page = await api.get<PaginatedChats>('/chat/active?limit=100')
  const ids = page.chats.map((chat) => chat.chat_id)
  for (const id of ids) {
    await api.patch(`/chat/${id}`, { archived: true })
  }
  return ids
}

// ── Projects (spec: /projects) ──────────────────────────────────────────────

export function createProject(
  name: string,
  description?: string,
): Promise<ProjectRead> {
  return api.post<ProjectRead>('/projects', { name, description })
}

export function deleteProject(projectId: string): Promise<void> {
  return api.delete(`/projects/${projectId}`)
}

/** PATCH /projects/{id} — one field at a time, like PATCH /auth/me. */
export function updateProject(
  projectId: string,
  fields: {
    name?: string
    description?: string
    custom_instructions?: string
    shared?: boolean
    archived?: boolean
  },
): Promise<ProjectRead> {
  return api.patch<ProjectRead>(`/projects/${projectId}`, fields)
}

/** POST /files/upload with project_id instead of chat_id — the file becomes a
 * project reference file every chat in the project can Read. */
export function uploadProjectFile(
  file: File,
  projectId: string,
): Promise<FileResponse> {
  const form = new FormData()
  form.append('file', file)
  form.append('project_id', projectId)
  return api.post<FileResponse>('/files/upload', form)
}

export function deleteProjectFile(
  projectId: string,
  fileId: string,
): Promise<void> {
  return api.delete(`/projects/${projectId}/files/${fileId}`)
}

// ── Files & artifacts (spec: /files, /artifacts) ────────────────────────────

export function uploadFile(file: File, chatId: string): Promise<FileResponse> {
  const form = new FormData()
  form.append('file', file)
  form.append('chat_id', chatId)
  return api.post<FileResponse>('/files/upload', form)
}

/** Authenticated binary GET (artifact/file endpoints require the Bearer
 * header, so a plain <a href> or <iframe src> can't fetch them directly). */
async function fetchBlob(path: string): Promise<Blob> {
  const token = useAuthStore.getState().accessToken
  const res = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  })
  if (!res.ok) throw new ApiError(res.status, null)
  return res.blob()
}

function downloadBlob(path: string, filename: string): Promise<void> {
  return fetchBlob(path).then((blob) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  })
}

const artifactPath = (chatId: string, path: string) =>
  `/artifacts/${chatId}?path=${encodeURIComponent(path)}`
const uploadedFilePath = (fileId: string) => `/files/${fileId}/download`

/** GET /artifacts/{chat_id}?path= — generated artifacts (/mnt/outputs/…). */
export function downloadArtifact(
  chatId: string,
  path: string,
  filename: string,
): Promise<void> {
  return downloadBlob(artifactPath(chatId, path), filename)
}

/** GET /files/{file_id}/download — user-uploaded files. */
export function downloadUploadedFile(
  fileId: string,
  filename: string,
): Promise<void> {
  return downloadBlob(uploadedFilePath(fileId), filename)
}

/** Same endpoints as above, but for in-panel preview (FilePreviewPanel) —
 * returns the blob instead of triggering a browser download. */
export function fetchArtifactBlob(chatId: string, path: string): Promise<Blob> {
  return fetchBlob(artifactPath(chatId, path))
}
export function fetchUploadedFileBlob(fileId: string): Promise<Blob> {
  return fetchBlob(uploadedFilePath(fileId))
}

/** GET /chat/{id}/export/pdf — the server renders the WHOLE conversation
 * (formatted headings/tables/code, no page limit), unlike the client-side
 * per-message export which only has the loaded turn's text. */
export function downloadChatPdf(
  chatId: string,
  filename: string,
): Promise<void> {
  return downloadBlob(`/chat/${chatId}/export/pdf`, filename)
}

/** Same renderer, one answer: the client only holds prose, so charts, result
 * tables and image artifacts would otherwise be dropped from a per-message
 * export (user 2026-07-30). */
export function downloadMessagePdf(
  chatId: string,
  responseId: string,
  filename: string,
): Promise<void> {
  return downloadBlob(
    `/chat/${chatId}/export/pdf?response_id=${encodeURIComponent(responseId)}`,
    filename,
  )
}

/** GET /chat/{id}/messages/{response_id}/speech — Read Aloud audio (AWS
 * Polly, standard engine; server-side only reads the message's own text,
 * skipping thinking/tool_use/tool_result and pointing at code blocks instead
 * of reading them — see services/speech.py). Returned as a Blob to play, not
 * download. */
export function fetchMessageSpeech(
  chatId: string,
  responseId: string,
): Promise<Blob> {
  return fetchBlob(`/chat/${chatId}/messages/${responseId}/speech`)
}

/** POST /dictate/transcribe — Composer's mic button (AWS Transcribe, batch
 * job under the hood; see services/dictate.py). Sends the whole recorded
 * clip once the user hits stop, gets back the transcript text. */
export async function transcribeAudio(audio: Blob): Promise<string> {
  const form = new FormData()
  form.append('audio', audio, 'dictation.wav')
  const { text } = await api.post<{ text: string }>('/dictate/transcribe', form)
  return text
}
